/**
 * The Journal Cover Letter Generator: a submission letter assembled from the author's
 * own findings and pitch, the standard declarations, and optional reviewer requests.
 *
 * Three phrasings of the opening, the fit sentence and the close (`v1`–`v3`), each in a
 * sole-author and a co-authored form. The bank is spread into the widget's strings; frame
 * syntax is documented in `compose.ts`.
 */

import { t } from "@/lib/i18n/interpolate";
import {
  addressName,
  asFragment,
  asSentence,
  cleanLine,
  compose,
  emphasis,
  fillFrames,
  orPlaceholder,
  splitItems,
  stripHonorific,
  withIndefiniteArticle,
} from "@/lib/tool-kit/correspondence/compose";

export const ARTICLE_TYPES = [
  "research",
  "review",
  "systematic",
  "short",
  "methods",
  "caseReport",
  "perspective",
  "other",
] as const;

export type ArticleType = (typeof ARTICLE_TYPES)[number];

export const EDITOR_TITLES = ["dr", "drNoStop", "professor", "none"] as const;

export type EditorTitle = (typeof EDITOR_TITLES)[number];

export const LETTER_SIGN_OFFS = [
  "sincerely",
  "yoursSincerely",
  "kindRegards",
  "bestRegards",
] as const;

export type LetterSignOff = (typeof LETTER_SIGN_OFFS)[number];

export type CompetingInterests = "none" | "declare";

/** Three phrasings of the letter's set pieces. */
export const PHRASINGS = ["v1", "v2", "v3"] as const;

export type Phrasing = (typeof PHRASINGS)[number];

const BULLET = "• ";
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A date input's YYYY-MM-DD, written the way the reader's language writes dates
 * ("October 5, 2026"; "5 October 2026" in en-GB). Built from the parts rather than
 * `new Date(iso)`, which reads a bare date as UTC midnight and can show the day before.
 */
export function formatLetterDate(iso: string, locale: string): string {
  const match = ISO_DATE.exec(iso);
  if (!match) {
    return "";
  }
  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3])
  );
  return new Intl.DateTimeFormat(locale, { dateStyle: "long" }).format(date);
}

export const COVER_LETTER_BANK = {
  editorTitles: {
    dr: "Dr.",
    drNoStop: "Dr",
    professor: "Professor",
  },
  greeting: "Dear {name},",
  greetingNoName: "Dear Editor,",
  articleTypes: {
    research: "an original research article",
    review: "a review article",
    systematic: "a systematic review",
    short: "a short communication",
    methods: "a methods article",
    caseReport: "a case report",
    perspective: "a perspective article",
  },
  signOffs: {
    sincerely: "Sincerely,",
    yoursSincerely: "Yours sincerely,",
    kindRegards: "Kind regards,",
    bestRegards: "Best regards,",
  },
  correspondingAuthor: "Corresponding author",
  placeholders: {
    title: "Manuscript title",
    journal: "Journal name",
    articleType: "article type",
    findings: "Your main findings, in two or three sentences",
    significance: "Why the work matters",
    fit: "why the work suits the journal",
    competing: "the competing interests",
    name: "Your name",
    affiliation: "Affiliation",
    email: "Email address",
  },
  suggestedIntro: [
    "!sole:We suggest the following potential reviewers, who have expertise in this area:",
    "?sole:I suggest the following potential reviewers, who have expertise in this area:",
  ],
  opposedIntro: [
    "!sole:We request that the following people not be invited to review this manuscript:",
    "?sole:I request that the following people not be invited to review this manuscript:",
  ],
  letter: [
    "?v1!sole:We are pleased to submit our manuscript “{title}” for consideration as {articleType} in {journal}.",
    "?v1?sole:I am pleased to submit my manuscript “{title}” for consideration as {articleType} in {journal}.",
    "?v2!sole:On behalf of all authors, I would like to submit our manuscript “{title}” for consideration as {articleType} in {journal}.",
    "?v2?sole:I would like to submit my manuscript “{title}” for consideration as {articleType} in {journal}.",
    "?v3!sole:Please consider our manuscript “{title}” for publication as {articleType} in {journal}.",
    "?v3?sole:Please consider my manuscript “{title}” for publication as {articleType} in {journal}.",
    "¶",
    "{findings}",
    "¶",
    "{significance}",
    "?v1!sole:We believe the manuscript will interest the readers of {journal} because {fit}.",
    "?v1?sole:I believe the manuscript will interest the readers of {journal} because {fit}.",
    "?v2:The work fits the scope of {journal} because {fit}.",
    "?v3:{journal} is a natural home for this work because {fit}.",
    "¶",
    "?original?notUnder!sole:We confirm that this manuscript is original, has not been published elsewhere, and is not under consideration by any other journal.",
    "?original?notUnder?sole:I confirm that this manuscript is original, has not been published elsewhere, and is not under consideration by any other journal.",
    "?original!notUnder!sole:We confirm that this manuscript is original and has not been published elsewhere.",
    "?original!notUnder?sole:I confirm that this manuscript is original and has not been published elsewhere.",
    "!original?notUnder!sole:We confirm that this manuscript is not under consideration by any other journal.",
    "!original?notUnder?sole:I confirm that this manuscript is not under consideration by any other journal.",
    "?preprint:A preprint of the manuscript is available at {preprint}.",
    "?approved:All authors have read and approved the manuscript and agree with its submission to {journal}.",
    "?competingNone!sole:The authors declare no competing interests.",
    "?competingNone?sole:I have no competing interests to declare.",
    "?competingDeclare!sole:The authors declare the following competing interests: {competing}.",
    "?competingDeclare?sole:I declare the following competing interests: {competing}.",
    "?other:{other}",
  ],
  closing: [
    "?v1!sole:Thank you for considering our manuscript. We look forward to hearing from you.",
    "?v1?sole:Thank you for considering my manuscript. I look forward to hearing from you.",
    "?v2:Thank you for your time and consideration. Please do not hesitate to contact me if you need any further information.",
    "?v3!sole:We appreciate your consideration and look forward to your response.",
    "?v3?sole:I appreciate your consideration and look forward to your response.",
  ],
} as const;

export interface CoverLetterBank {
  editorTitles: Readonly<Record<Exclude<EditorTitle, "none">, string>>;
  greeting: string;
  greetingNoName: string;
  articleTypes: Readonly<Record<Exclude<ArticleType, "other">, string>>;
  signOffs: Readonly<Record<LetterSignOff, string>>;
  correspondingAuthor: string;
  placeholders: Readonly<Record<string, string>>;
  suggestedIntro: readonly string[];
  opposedIntro: readonly string[];
  letter: readonly string[];
  closing: readonly string[];
}

export interface CoverLetterInput {
  /** Already formatted for the reader's locale, or "" for no date line. */
  date: string;
  editorTitle: EditorTitle;
  editorName: string;
  journal: string;
  title: string;
  articleType: ArticleType;
  /** The journal's own name for the article type, when `articleType` is "other". */
  otherType: string;
  soleAuthor: boolean;
  findings: string;
  significance: string;
  fit: string;
  suggested: string;
  opposed: string;
  original: boolean;
  notUnderConsideration: boolean;
  allApproved: boolean;
  competing: CompetingInterests;
  competingText: string;
  preprint: string;
  other: string;
  name: string;
  affiliation: string;
  email: string;
  signOff: LetterSignOff;
  phrasing: Phrasing;
}

function editorGreeting(input: CoverLetterInput, bank: CoverLetterBank) {
  const name = stripHonorific(input.editorName);
  if (name === "") {
    return bank.greetingNoName;
  }
  const title =
    input.editorTitle === "none" ? "" : bank.editorTitles[input.editorTitle];
  return t(bank.greeting, { name: addressName(title, name) });
}

function articleTypeValue(input: CoverLetterInput, bank: CoverLetterBank) {
  if (input.articleType !== "other") {
    return bank.articleTypes[input.articleType];
  }
  return orPlaceholder(
    withIndefiniteArticle(asFragment(input.otherType)),
    bank.placeholders.articleType ?? ""
  );
}

function letterFlags(input: CoverLetterInput, values: Record<string, string>) {
  const flags = new Set<string>([input.phrasing]);
  const set = (name: string, on: boolean) => {
    if (on) {
      flags.add(name);
    }
  };
  set("sole", input.soleAuthor);
  set("original", input.original);
  set("notUnder", input.notUnderConsideration);
  set("approved", input.allApproved && !input.soleAuthor);
  set("competingNone", input.competing === "none");
  set("competingDeclare", input.competing === "declare");
  set("preprint", values.preprint !== "");
  set("other", values.other !== "");
  return flags;
}

function listParagraph(
  intro: readonly string[],
  raw: string,
  values: Record<string, string>,
  flags: ReadonlySet<string>
): string | null {
  const people = splitItems(raw);
  const heading = fillFrames(intro, values, flags)[0];
  if (people.length === 0 || !heading) {
    return null;
  }
  return [heading, ...people.map((person) => `${BULLET}${person}`)].join("\n");
}

/** The letter as marked paragraphs, date and greeting first, signature last. */
export function buildCoverLetter(
  input: CoverLetterInput,
  bank: CoverLetterBank
): string[] {
  const ph = (key: string) => bank.placeholders[key] ?? key;
  const values: Record<string, string> = {
    title: orPlaceholder(asFragment(input.title), ph("title")),
    journal: emphasis(orPlaceholder(asFragment(input.journal), ph("journal"))),
    articleType: articleTypeValue(input, bank),
    findings: orPlaceholder(asSentence(input.findings), ph("findings")),
    significance: orPlaceholder(
      asSentence(input.significance),
      ph("significance")
    ),
    fit: orPlaceholder(asFragment(input.fit), ph("fit")),
    competing: orPlaceholder(asFragment(input.competingText), ph("competing")),
    preprint: asFragment(input.preprint),
    other: asSentence(input.other),
  };
  const flags = letterFlags(input, values);

  const paragraphs: string[] = [];
  const date = cleanLine(input.date);
  if (date !== "") {
    paragraphs.push(date);
  }
  paragraphs.push(editorGreeting(input, bank));
  paragraphs.push(...compose(bank.letter, values, flags));
  for (const [intro, raw] of [
    [bank.suggestedIntro, input.suggested],
    [bank.opposedIntro, input.opposed],
  ] as const) {
    const list = listParagraph(intro, raw, values, flags);
    if (list) {
      paragraphs.push(list);
    }
  }
  paragraphs.push(...compose(bank.closing, values, flags));

  const signature = [
    bank.signOffs[input.signOff],
    orPlaceholder(cleanLine(input.name), ph("name")),
  ];
  if (!input.soleAuthor) {
    signature.push(bank.correspondingAuthor);
  }
  signature.push(
    orPlaceholder(cleanLine(input.affiliation), ph("affiliation")),
    orPlaceholder(cleanLine(input.email), ph("email"))
  );
  paragraphs.push(signature.join("\n"));
  return paragraphs;
}
